import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { User as FirebaseUser } from "firebase/auth";
import { AuthenticationService } from "@/modules/identity/services/AuthenticationService";
import type { AppUser } from "@/types/user";

interface AuthContextValue {
  firebaseUser: FirebaseUser | null;
  profile: AppUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  async function loadProfile(uid: string) {
    const userProfile = await AuthenticationService.getUserProfile(uid);
    setProfile(userProfile);
  }

  useEffect(() => {
    const unsubscribe = AuthenticationService.onAuthStateChanged(async (user) => {
      setFirebaseUser(user);
      if (user) {
        await loadProfile(user.uid);
      } else {
        setProfile(null);
      }
      setIsLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      firebaseUser,
      profile,
      isLoading,
      isAuthenticated: Boolean(firebaseUser),
      refreshProfile: async () => {
        if (firebaseUser) await loadProfile(firebaseUser.uid);
      },
      signOut: () => AuthenticationService.signOut(),
    }),
    [firebaseUser, profile, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth deve ser usado dentro de um <AuthProvider>");
  return context;
}
